import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { usePaginateQuery } from "@/hooks";
import { api } from "@/lib/api";
import { type Paginate, type SearchRequest } from "@/types";

import { type Entry } from "@/modules/handbook/user/types";

export type EntryList = Paginate<Entry>;

export const entries = createQueryKeys("handbook-entries", {
  all: (page?: number, filter?: SearchRequest) => [{ page, filter }],
  one: (id?: number) => [id],
});

const include = "categories,user";

export const fetchEntriesQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...entries.all(page, search),
    queryFn: () =>
      api
        .get<EntryList>(`/handbook/entries`, {
          params: { page, include, ...search },
        })
        .then((response) => response.data),
  });

export const useFetchEntries = (search?: SearchRequest) =>
  usePaginateQuery((page) => fetchEntriesQueryOptions(page, search));

export const fetchEntryQueryOptions = (id?: number) =>
  queryOptions({
    ...entries.one(id),
    queryFn: () =>
      api
        .get<Entry>(`/handbook/entries/${id}`)
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchEntry = (id?: number) =>
  useQuery(fetchEntryQueryOptions(id));

export const useFetchBookmarkEntries = () =>
  useFetchEntries({ filters: { is_bookmarked: 1 } });

export const useBookmarkEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number }) =>
      api
        .put(`/handbook/entries/${id}/bookmark`)
        .then((response) => response.data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: entries.all._def,
      });
      queryClient.invalidateQueries(entries.one(id));
    },
  });
};

export const useUnbookmarkEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number }) =>
      api
        .delete(`/handbook/entries/${id}/bookmark`)
        .then((response) => response.data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: entries.all._def,
      });
      queryClient.invalidateQueries(entries.one(id));
    },
  });
};
